import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import useTranslation from "../../../shared/hooks/useTranslation";
import { useAppSelector } from "../../../shared/hooks/sharedHooks";
import { CustomerPartnershipsState, PartnerInvitation } from "../models/models";
import dt from "../utils/dt";

interface AcceptInvitationDialogProps {
  open: boolean;
  invitation: PartnerInvitation | null;
  onConfirm: () => void;
  onClose: () => void;
}

const AcceptInvitationDialog: React.FC<AcceptInvitationDialogProps> = ({
  open,
  invitation,
  onConfirm,
  onClose,
}) => {
  const { translate } = useTranslation("partnerships");
  const { actionLoading } = useAppSelector<CustomerPartnershipsState>(
    (state: any) => state.customerPartnerships
  );

  if (!invitation) return null;

  const companyName = invitation.inviterCustomerName || invitation.inviteeEmail;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: dt.radiusLg,
          fontFamily: dt.font,
          "& *:not(.MuiSvgIcon-root)": { fontFamily: dt.font },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
          fontWeight: 700,
          fontSize: "20px",
          color: dt.textPrimary,
        }}
      >
        {translate("partnerships:accept.dialogTitle")}
        <IconButton onClick={onClose} size="small" disabled={actionLoading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Box sx={{ color: dt.textSecondary, fontSize: "14px", mb: 2 }}>
          {translate("partnerships:accept.dialogDescription", { companyName })}
        </Box>

        {/* Value explanation bullets */}
        <Box
          sx={{
            backgroundColor: dt.inputBg,
            borderRadius: dt.radiusSm,
            padding: "14px 16px",
            mb: 2,
          }}
        >
          <Box
            sx={{
              fontSize: "13px",
              fontWeight: 600,
              color: dt.textPrimary,
              mb: 1,
            }}
          >
            By accepting, you'll be able to:
          </Box>
          <Box component="ul" sx={{ margin: 0, paddingLeft: "20px" }}>
            <Box
              component="li"
              sx={{ fontSize: "13px", color: dt.textSecondary, lineHeight: 1.8 }}
            >
              {translate("acceptDialog.benefitNetwork")}
            </Box>
            <Box
              component="li"
              sx={{ fontSize: "13px", color: dt.textSecondary, lineHeight: 1.8 }}
            >
              {translate("acceptDialog.benefitDirectRequests")}
            </Box>
            <Box
              component="li"
              sx={{ fontSize: "13px", color: dt.textSecondary, lineHeight: 1.8 }}
            >
              {translate("acceptDialog.benefitTiers")}
            </Box>
          </Box>
        </Box>

        {invitation.message && (
          <Box
            sx={{
              backgroundColor: dt.inputBg,
              borderRadius: dt.radiusSm,
              padding: "14px 16px",
            }}
          >
            <Box
              sx={{
                fontSize: "12px",
                fontWeight: 600,
                color: dt.textMuted,
                textTransform: "uppercase",
                letterSpacing: "1px",
                mb: 0.75,
              }}
            >
              {translate("partnerships:accept.theirMessage")}
            </Box>
            <Box
              sx={{
                fontSize: "14px",
                color: dt.textPrimary,
                fontStyle: "italic",
                lineHeight: 1.6,
              }}
            >
              "{invitation.message}"
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={actionLoading}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "14px",
            color: dt.textSecondary,
            borderRadius: dt.radiusSm,
            px: 2.5,
          }}
        >
          {translate("partnerships:accept.cancel")}
        </Button>
        <Button
          onClick={onConfirm}
          disabled={actionLoading}
          variant="contained"
          startIcon={
            actionLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <CheckCircleOutlineIcon sx={{ fontSize: "18px !important" }} />
            )
          }
          sx={{
            background: "linear-gradient(135deg, #27AE60 0%, #219A52 100%)",
            borderRadius: dt.radiusSm,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "14px",
            px: 3,
            boxShadow: "0 2px 8px rgba(39,174,96,0.25)",
            "&:hover": {
              background: "linear-gradient(135deg, #219A52 0%, #27AE60 100%)",
              boxShadow: "0 4px 16px rgba(39,174,96,0.3)",
            },
          }}
        >
          {translate("partnerships:accept.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AcceptInvitationDialog;
